import { PaymentMethod } from '@fox/finance';
import { OptionsInput, SwiftFoxPlansResult, SwiftFoxSubscriptionDetailed } from '../contracts';
import { WebPaymentType } from './renew-subscription';

export type SubscriptionState = {
	initialState: {
		subscriptionId: string;
		selectedOptions: Array<OptionsInput>;
		isAutoRenew: boolean;
		nominatedFee?: number;
		selectedPlanId?: string;
		selectedFeeId?: string;
		selectedPayment?: WebPaymentType;
	};
	subscriptionId: string;
	selectedOptions: Array<OptionsInput>;
	isAutoRenew: boolean;
	nominatedFee?: number;
	selectedPlanId?: string;
	selectedFeeId?: string;
	selectedPayment?: WebPaymentType;
};

type SubscriptionActionType =
	| { type: 'selectPlanById'; data: string }
	| { type: 'toggleOption'; data: string; limit?: number }
	| { type: 'selectFeeById'; data: string }
	| { type: 'setIsAutoRenew'; data: boolean }
	| { type: 'updatePartial'; data: Partial<SubscriptionState> }
	| { type: 'selectPayment'; data: WebPaymentType }
	| { type: 'resetState' }
	| { type: 'resetInitialState' }
	| { type: 'resetSelectedPayment' };

export function subscriptionStateReducer<T>(state: SubscriptionState, action: SubscriptionActionType): SubscriptionState {
	switch (action.type) {
		case 'selectPlanById':
			return {
				...state,
				selectedPlanId: action.data
			};
		case 'selectFeeById':
			return {
				...state,
				selectedFeeId: action.data
			};
		case 'setIsAutoRenew':
			return { ...state, isAutoRenew: action.data };
		case 'selectPayment':
			return { ...state, selectedPayment: action.data };
		case 'resetSelectedPayment':
			return { ...state, selectedPayment: state.initialState.selectedPayment };
		case 'resetState':
			return { ...state.initialState, initialState: state.initialState };
		case 'resetInitialState':
			return {
				...state,
				initialState: {
					isAutoRenew: state.isAutoRenew,
					selectedOptions: state.selectedOptions,
					subscriptionId: state.subscriptionId,
					selectedFeeId: state.selectedFeeId,
					selectedPayment: state.selectedPayment,
					selectedPlanId: state.selectedPlanId,
					nominatedFee: state.nominatedFee
				}
			};
		case 'toggleOption':
			return {
				...state,
				selectedOptions: state.selectedOptions.some(o => o.id === action.data)
					? state.selectedOptions.filter(a => a.id !== action.data)
					: [...state.selectedOptions, { id: action.data, reset: false, limit: action.limit }]
			};
		case 'updatePartial':
			return { ...state, ...action.data };
	}
}

export function getInitialState(
	subscription: SwiftFoxSubscriptionDetailed,
	plan: SwiftFoxPlansResult
): Omit<SubscriptionState, 'initialState'> {
	return {
		subscriptionId: subscription.id,
		selectedPlanId: plan.id,
		selectedOptions: subscription.options,
		selectedFeeId: subscription.fee.id,
		selectedPayment: subscription?.directDebitId
			? ({
					directDebitId: subscription?.directDebitId,
					method: PaymentMethod.DirectDebit,
					provider: subscription?.provider
			  } as WebPaymentType)
			: subscription?.creditCardId
			? ({
					creditCardId: subscription?.creditCardId,
					method: PaymentMethod.CreditCard,
					provider: subscription?.provider
			  } as WebPaymentType)
			: undefined,
		isAutoRenew: subscription.autoRenew,
		nominatedFee: subscription.nominatedFee
	};
}
